import {
  GameSessionResult,
  GameSessionReviewStatus,
} from '../../../assets/enums/game-session.enum.ts';
import type { Nullable } from '../../types.ts';
import {
  type PerformanceReviewAgeBucket,
  type PerformanceReviewQueue,
} from '../performance-api.ts';
import { centsToDollars, toCents } from './money.ts';
import { type SessionsServiceSession } from './sessions-service-api.ts';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const AGE_BUCKETS: PerformanceReviewAgeBucket[] = [
  'under_1_day',
  'from_1_to_7_days',
  'from_7_to_30_days',
  'over_30_days',
];

const getAgeBucket = (ageMs: number): PerformanceReviewAgeBucket => {
  if (ageMs < DAY_MS) return 'under_1_day';
  if (ageMs < 7 * DAY_MS) return 'from_1_to_7_days';
  if (ageMs < 30 * DAY_MS) return 'from_7_to_30_days';
  return 'over_30_days';
};

const percentile = (sorted: number[], fraction: number): Nullable<number> =>
  sorted.length ? sorted[Math.ceil(fraction * sorted.length) - 1] : null;

const getCompletedAt = (session: SessionsServiceSession) =>
  session.completed_at ?? session.created_at;

export const buildReviewQueue = (
  completed: SessionsServiceSession[],
  now: Date
): PerformanceReviewQueue => {
  const awaiting = completed.filter(
    (session) => session.review_status === GameSessionReviewStatus.AwaitingApproval
  );
  const awaitingBuckets = awaiting.map((session) =>
    getAgeBucket(now.getTime() - Date.parse(getCompletedAt(session)))
  );

  const reviewHours = completed
    .flatMap(({ reviewed_at, completed_at }) =>
      reviewed_at && completed_at
        ? [(Date.parse(reviewed_at) - Date.parse(completed_at)) / HOUR_MS]
        : []
    )
    .sort((a, b) => a - b);

  const wonAwaiting = awaiting.filter((session) => session.result === GameSessionResult.Won);

  return {
    awaiting: awaiting.length,
    oldest_completed_at: awaiting.map(getCompletedAt).sort()[0] ?? null,
    aging: AGE_BUCKETS.map((bucket) => ({
      bucket,
      count: awaitingBuckets.filter((sessionBucket) => sessionBucket === bucket).length,
    })),
    reviewed: reviewHours.length,
    median_review_hours: percentile(reviewHours, 0.5),
    p90_review_hours: percentile(reviewHours, 0.9),
    won_awaiting: wonAwaiting.length,
    won_awaiting_payouts: centsToDollars(
      wonAwaiting.reduce((sum, session) => sum + toCents(session.final_payout), 0)
    ),
  };
};
